"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SearchCheck, FileCode2, Building2, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  number: "01",
  title: "Technical & Content SEO Audit",
  phase: "Phase 1: Diagnosis",
  description: "I perform a comprehensive audit to uncover technical indexation issues, site architecture bottlenecks, and content gap opportunities that prevent your business from ranking.",
  deliverables: "Comprehensive Audit Report, Architectural Roadmap, Keyword Matrix",
  icon: SearchCheck
}, {
  number: "02",
  title: "On-Page & Entity Optimization",
  phase: "Phase 2: On-Site Strategy",
  description: "I refine target landing pages, optimize semantic headers, align content with intent, and build strong internal linking to maximize search engine authority.",
  deliverables: "Optimized Landing Pages, Content Briefs, Internal Link Structure",
  icon: FileCode2
}, {
  number: "03",
  title: "Citations & Brand Authority",
  phase: "Phase 3: Authority Building",
  description: "I establish accurate local and national business citations, clean up inconsistent NAP data, and earn high-quality, relevant digital placements.",
  deliverables: "Citation Cleanup, Verified Directory Listings, High-Authority Mentions",
  icon: Building2
}, {
  number: "04",
  title: "Review Strategy & Trust Signals",
  phase: "Phase 4: Reputation",
  description: "I implement genuine review acquisition workflows that build conversion trust, strengthen Google Business profile prominence, and convert organic traffic.",
  deliverables: "Review Automation Framework, Trust Signal Placement, GBP Optimization",
  icon: Star
}, {
  number: "05",
  title: "Schema Markup & AI Search Prep",
  phase: "Phase 5: Future-Proofing",
  description: "I deploy structured JSON-LD schema across key pages so Google and modern AI search engines accurately understand and feature your core brand services.",
  deliverables: "Custom Schema Implementation, Entity Validation, AI Search Optimization",
  icon: Code2
}, {
  number: "06",
  title: "Transparent Reporting & Growth",
  phase: "Phase 6: Performance",
  description: "I provide clear, monthly reporting focused on real business metrics: organic qualified leads, phone calls, key term positions, and measurable revenue impact.",
  deliverables: "Live Executive Dashboard, Monthly Strategy Calls, Lead Tracking",
  icon: BarChart3
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".methodology-card", {
        y: 35
      });
      gsap.to(".methodology-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".connector-line", {
        scaleY: 0,
        transformOrigin: "top center"
      });
      gsap.to(".connector-line", {
        scaleY: 1,
        duration: 1,
        ease: "power2.inOut",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".methodology-card", {
        opacity: 1,
        y: 0
      });
      gsap.set(".connector-line", {
        scaleY: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyworkflow" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <Badge variant="secondary" className="mb-4 px-3 py-1 bg-secondary text-secondary-foreground text-xs font-semibold tracking-wider uppercase border border-accent/20">
            My SEO Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-foreground">
            A Hands-On Methodology Built for Sustainable Search Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When you hire me, you work directly with me. Here is the structured, step-by-step workflow I use to diagnose search barriers, optimize site architecture, and drive high-converting organic results.
          </p>
        </div>

        <div className="relative">
          <div className="connector-line hidden lg:block absolute left-1/2 top-12 bottom-12 w-0.5 bg-accent/30 -translate-x-1/2 z-0" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 relative z-10">
            {processSteps.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.number} data-index={i} className="methodology-card flex flex-col h-full" style={{
              opacity: 0
            }}>
                  <Card className="flex-1 bg-card border border-border shadow-md hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden group">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <span className="inline-flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg font-mono shadow-sm group-hover:scale-105 transition-transform">
                          {step.number}
                        </span>
                        <span className="text-xs font-semibold uppercase tracking-wider text-accent-foreground bg-accent/80 px-3 py-1 rounded-full">
                          {step.phase}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <CardTitle className="text-xl font-bold font-default text-card-foreground">
                          {step.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {step.description}
                      </CardDescription>

                      <Separator className="bg-border" />

                      <div className="space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block">
                          Key Deliverables:
                        </span>
                        <div className="flex items-start gap-2 text-xs font-medium text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{step.deliverables}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-16 md:mt-24">
          <Card className="bg-muted/60 border border-border rounded-2xl p-8 sm:p-10 shadow-sm relative overflow-hidden">
            <div className="absolute right-0 top-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
              <div className="space-y-2 text-center md:text-left max-w-2xl">
                <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground">
                  Ready to Start Your Targeted SEO Campaign?
                </h3>
                <p className="text-muted-foreground text-sm sm:text-base">
                  Let us review your current search standings and formulate a tailored strategy tailored specifically for your business goals.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full md:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                  <Link to="/ContactPage">
                    Book Free Consultation
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                  <Link to="/CaseStudiesPage">
                    Explore Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}